import BaseEntity             from '@/core/misc/BaseEntity';
import Core                   from '@/core/Core';
import serializeSimplified    from '@/core/misc/decorators/SerializeSimplified';
import Title_ru_RU_Class      from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU            from '@/core/misc/decorators/Title_ru_RU';
import TYPES                  from '@/core/services/Changelogs/types';
import TransportZone          from '@/domains/TransportZones/TransportZone';
import Price                  from "@/core/types/Price";
import Currency               from "@/domains/Currencies/Currency";
import CurrenciesService      from "@/domains/Currencies/CurrenciesService";
import NoSerialize            from "@/core/misc/decorators/NoSerialize";
import Vehicle                from "@/domains/Vehicles/Vehicle"
import VehicleType            from "@/domains/VehicleTypes/VehicleType"

/**
 * Маршрут доставки между двумя транспортными зонами.
 * Хранит цены за кг и за м³ в валюте costCurrencyCode.
 *
 * Цены отдаются как есть, в валюте тарифа: сам Price валюту не несёт, а
 * приводить её к валюте потребителя маршрут не обязан. Конвертацию делает
 * потребитель — ему известны и целевая валюта, и дата курса (обычно
 * AbstractOrderOption.getDateForCurrencyRate()).
 */
@Title_ru_RU_Class('Маршрут')
class TransportRoute extends BaseEntity {

  static Events = {
    Created: 'TransportRoute:created',
    Updated: 'TransportRoute:updated',
    Deleted: 'TransportRoute:deleted',
  } as const;

  @Title_ru_RU('Зона отправления', TYPES.OBJECT)
  @serializeSimplified
  public zoneFrom: TransportZone;

  @Title_ru_RU('Зона назначения', TYPES.OBJECT)
  @serializeSimplified
  public zoneTo: TransportZone;

  @Title_ru_RU('Валюта тарифа', TYPES.STRING)
  protected _costCurrencyCode: string | null = null;

  /** Валюта тарифа объектом. Выводится из кода, на бек не уезжает — там
   *  хранится код. */
  @NoSerialize
  protected _costCurrency: Currency | null = null;

  @Title_ru_RU('Цена за кг', TYPES.NUMBER)
  public costPerKg: Price;

  @Title_ru_RU('Цена за м³', TYPES.NUMBER)
  public costPerM3: Price;

  @Title_ru_RU('Транспорт', TYPES.OBJECT)
  public vehicle: Vehicle | null = null;

  @Title_ru_RU('Тип транспорта', TYPES.OBJECT)
  public vehicleType: VehicleType | null = null;

  @Title_ru_RU('Ставка за рейс', TYPES.PRICE)
  public ftlCost: Price | null = null;


  get costCurrencyCode(): string | null {
    return this._costCurrencyCode;
  }

  /** Код — единственный источник истины: объект валюты выводится здесь,
   *  поэтому потребителям не нужен переходник вида computed { get, set }. */
  set costCurrencyCode(value: string | null) {
    this._costCurrencyCode = value;

    if (this._costCurrencyCode) {
      this._costCurrency = CurrenciesService.currencies.get(this._costCurrencyCode) ?? null;
    } else {
      this._costCurrency = null;
    }
  }

  get costCurrency(): Currency | null {
    return this._costCurrency;
  }

  /** Пишем через сеттер кода, чтобы согласованность держалась в одном месте. */
  set costCurrency(value: Currency | null) {
    this.costCurrencyCode = value?.code ?? null;
  }

  /**
   * Гидратирует маршрут из данных сервера
   */
  static fromServer(data: any, result = new TransportRoute()): TransportRoute {
    super.fromServer(data, result);

    result.zoneFrom = Core.utils.hydrate(data.zoneFrom, TransportZone.fromServer)!;
    result.zoneTo = Core.utils.hydrate(data.zoneTo, TransportZone.fromServer)!;

    result.costPerKg = Core.utils.hydrate(data.costPerKg, Price.fromServer)!;
    result.costPerM3 = Core.utils.hydrate(data.costPerM3, Price.fromServer)!;

    /** Через сеттер, а не в приватное поле: он же выводит объект валюты. */
    result.costCurrencyCode = data.costCurrencyCode ?? null;

    result.vehicle = Core.utils.hydrate(data.vehicle, Vehicle.fromServer);
    result.vehicleType = Core.utils.hydrate(data.vehicleType, VehicleType.fromServer);

    result.ftlCost = Core.utils.hydrate(data.ftlCost, Price.fromServer)

    return result;
  }

  /**
   * Создаёт новый пустой маршрут с валютой текущего партнёра по умолчанию
   */
  static fromNew(): TransportRoute {
    const route = new this();

    route.costCurrencyCode = Core.currentPartner.country.localCurrency.code;

    return route;
  }

  /**
   * Отображаемое имя маршрута: «Зона А — Зона Б»
   */
  public get name(): string {
    const from = this.zoneFrom?.name ?? this.zoneFrom?.id ?? '?';
    const to   = this.zoneTo?.name   ?? this.zoneTo?.id   ?? '?';
    return `${from} — ${to}`;
  }

  toString(): string {
    return this.name;
  }

  /**
   * Валидирует маршрут перед сохранением
   */
  validate(): string[] {
    const errors: string[] = [];

    if (!this.zoneFrom?.id) {
      errors.push('Укажите зону отправления');
    }
    if (!this.zoneTo?.id) {
      errors.push('Укажите зону назначения');
    }
    if (this.zoneFrom?.id && this.zoneTo?.id && this.zoneFrom.id === this.zoneTo.id) {
      errors.push('Зоны отправления и назначения должны различаться');
    }
    if (!this.costPerKg?.isPositive()) {
      errors.push('Укажите цену за кг');
    }
    if (!this.costPerM3?.isPositive()) {
      errors.push('Укажите цену за м³');
    }
    if (!this.costCurrencyCode) {
      errors.push('Не задана валюта тарифа');
    }

    return errors;
  }
}

export default TransportRoute;
